import { Modal } from '@/components/UI';
import {
    type AeroCandidateReviewRow,
    formatAeroNumber,
    formatAeroPercent,
    formatAeroWinRatePercent
} from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import ExitConfigChips from '@/features/tradeLogicAuthoring/components/ExitConfigChips';
import { type AeroObsPalette, getAeroObsTone } from '../aeroObsPalette';
import { formatAeroShortHash } from '../aeroPageUtils';

interface CandidateComparisonProps {
    isOpen: boolean;
    candidates: AeroCandidateReviewRow[];
    palette: AeroObsPalette;
    onClose: () => void;
}

interface ComparisonMetric {
    label: string;
    direction: 'higher' | 'lower';
    read: (candidate: AeroCandidateReviewRow) => number | null;
    format: (value: number | null) => string;
}

const METRICS: ComparisonMetric[] = [
    { label: 'Selected score', direction: 'higher', read: (c) => c.selectedLiveScore, format: formatAeroNumber },
    { label: 'Raw avg profit', direction: 'higher', read: (c) => c.avgProfit, format: formatAeroPercent },
    { label: 'Winsorized avg profit', direction: 'higher', read: (c) => c.winsorizedAvgProfit, format: formatAeroPercent },
    { label: 'Upper-tail cap', direction: 'lower', read: (c) => c.winsorizationCap, format: formatAeroPercent },
    { label: 'Win rate', direction: 'higher', read: (c) => c.winRate, format: formatAeroWinRatePercent },
    { label: 'Profit factor', direction: 'higher', read: (c) => c.profitFactor, format: formatAeroNumber },
    { label: 'Sharpe ratio', direction: 'higher', read: (c) => c.sharpeRatio, format: formatAeroNumber },
    { label: 'Drawdown', direction: 'lower', read: (c) => c.drawdown, format: formatAeroPercent },
    { label: 'Total trades', direction: 'higher', read: (c) => c.totalTrades, format: formatAeroNumber }
];

const findBestValue = (candidates: AeroCandidateReviewRow[], metric: ComparisonMetric): number | null => {
    const values = candidates.map(metric.read).filter((value): value is number => value != null && Number.isFinite(value));

    if (values.length === 0) return null;

    return metric.direction === 'higher' ? Math.max(...values) : Math.min(...values);
};

const CandidateComparison = ({ isOpen, candidates, palette, onClose }: CandidateComparisonProps) => {
    const showObsRow =
        palette.hasMultipleAxes ||
        candidates.some((candidate, index) => index > 0 && candidate.optionBuyingStrategyId !== candidates[0].optionBuyingStrategyId);

    return (
        <Modal isOpen={isOpen} size="xl" title={`Compare ${candidates.length} candidates`} onClose={onClose}>
            {candidates.length === 0 ? (
                <div className="text-sm text-zinc-400">Select candidates from the results table to compare them.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr>
                                <th className="border-b border-zinc-800 p-2 text-left text-xs uppercase tracking-wide text-zinc-500">
                                    Metric
                                </th>
                                {candidates.map((candidate, index) => {
                                    const tone = getAeroObsTone(palette, candidate.optionBuyingStrategyId);

                                    return (
                                        <th className="border-b border-zinc-800 p-2 text-left" key={candidate.variantHash}>
                                            <div className="flex items-center gap-1.5 font-medium text-zinc-100">
                                                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${tone.dotClassName}`} />
                                                <span>#{index + 1}</span>
                                            </div>
                                            <div className="font-mono text-xs text-zinc-500">
                                                {formatAeroShortHash(candidate.variantHash)}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {showObsRow && (
                                <tr>
                                    <td className="border-b border-zinc-800/60 p-2 align-top text-zinc-400">Axis</td>
                                    {candidates.map((candidate) => {
                                        const tone = getAeroObsTone(palette, candidate.optionBuyingStrategyId);

                                        return (
                                            <td className="border-b border-zinc-800/60 p-2 align-top" key={candidate.variantHash}>
                                                <span
                                                    className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium ${tone.chipClassName}`}
                                                    title={tone.label}
                                                >
                                                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dotClassName}`} />
                                                    {tone.shortLabel}
                                                </span>
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                            <tr>
                                <td className="border-b border-zinc-800/60 p-2 align-top text-zinc-400">Exit config</td>
                                {candidates.map((candidate) => (
                                    <td className="border-b border-zinc-800/60 p-2 align-top" key={candidate.variantHash}>
                                        <ExitConfigChips candidate={candidate} />
                                    </td>
                                ))}
                            </tr>
                            {METRICS.map((metric) => {
                                const bestValue = findBestValue(candidates, metric);

                                return (
                                    <tr key={metric.label}>
                                        <td className="border-b border-zinc-800/60 p-2 text-zinc-400">{metric.label}</td>
                                        {candidates.map((candidate) => {
                                            const value = metric.read(candidate);
                                            const isBest = bestValue !== null && value === bestValue && candidates.length > 1;

                                            return (
                                                <td
                                                    className={`border-b border-zinc-800/60 p-2 font-medium ${
                                                        isBest ? 'text-green-300' : 'text-zinc-200'
                                                    }`}
                                                    key={candidate.variantHash}
                                                >
                                                    {metric.format(value)}
                                                    {isBest && <span className="ml-1 text-xs text-green-500">best</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};

export default CandidateComparison;
